import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';

// P1 Görev Paketi A — PIP (Performans Geliştirme Planı) İK yönetim API'si.
// PipController rotaları (BE ile birebir):
//   GET  v1/pips?status[in]=&employeeId[eq]=&pageNumber=&pageSize=   (PM.Read + EA)
//   GET  v1/pips/{id}                                                 (PM.Read + EA)
//   POST v1/pips {employeeId, durationDays?, goals[]}                 (PM.Write + EA)
//   PUT  v1/pips/{id} {durationDays?, goals[]}                        (PM.Write + EA)
//   POST v1/pips/{id}/start                                           (PM.Write + EA)
//   POST v1/pips/{id}/check-ins {note}                                (PM.Write + EA)
//   POST v1/pips/{id}/close {outcome, closureNote, goals[]}           (PM.Write + EA)
//   POST v1/pips/{id}/cancel                                          (PM.Write + EA)

export const PIP_STATUSES = ['draft', 'active', 'completed_successful', 'completed_unsuccessful', 'cancelled'];

// 30/60/90 gün — Kolay İK/4857 pratiği; BE start ucu bu beyaz listeyi doğrular.
export const PIP_DURATIONS = [30, 60, 90];

// TeamGoalsPage/CALIBRATION_STATUS_COLORS rozet paletiyle tutarlı.
export const PIP_STATUS_COLORS = {
  draft: { color: '#D97706', bg: '#FFFBEB' },
  active: { color: '#0291EF', bg: '#EFF6FF' },
  completed_successful: { color: '#16A34A', bg: '#DCFCE7' },
  completed_unsuccessful: { color: '#DC2626', bg: '#FEF2F2' },
  cancelled: { color: '#6B7280', bg: '#F3F4F6' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const usePipApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/pips?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const getById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/pips/${id}`);
      return res.data?.data;
    } catch {
      return null;
    }
  }, []);

  const create = useCallback(async (payload) => {
    try {
      await api.post('/v1/pips', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/pips/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const start = useCallback(async (id) => {
    try {
      await api.post(`/v1/pips/${id}/start`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const addCheckIn = useCallback(async (id, note) => {
    try {
      await api.post(`/v1/pips/${id}/check-ins`, { note });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const close = useCallback(async (id, payload) => {
    try {
      await api.post(`/v1/pips/${id}/close`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try {
      await api.post(`/v1/pips/${id}/cancel`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeId', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.pip.employee'),
      // BE listesi yalnız status[in] + employeeId[eq] filtreler — isim filtresi
      // sunucuda karşılıksız kalacağı için bilerek kapalı (yanıltıcı UI olmasın).
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('performance.pip.statusLabel'),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customBodyRender: (v) => {
          const cfg = PIP_STATUS_COLORS[v] || PIP_STATUS_COLORS.draft;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`performance.pip.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
        filterOptions: {
          // Backend `List` yalnız `status[in]` okur (CSV) — development-plans
          // config'indeki custom select deseniyle aynı.
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.pip.statusLabel')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {PIP_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.pip.status.${s}`, { defaultValue: s })}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'durationDays',
      label: t('performance.pip.durationLabel'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v != null ? `${v} ${t('performance.pip.daysSuffix')}` : '-'),
      },
    },
    {
      name: 'startedAt',
      label: t('performance.pip.startedAt'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
      },
    },
    {
      name: 'dueDate',
      label: t('performance.pip.dueDate'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
      },
    },
    {
      name: 'sourcePeriodName',
      label: t('performance.pip.sourcePeriod'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'createDate',
      label: t('performance.common.createDate'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
      },
    },
  ], [t, lang]);

  return {
    fetching,
    data,
    pagination,
    columns,
    getById,
    create,
    update,
    start,
    addCheckIn,
    close,
    cancel,
    refresh,
  };
};

export default usePipApi;
